import { nowEpochMilliseconds } from "@nijiurachan/js/util/time";
import {
  create,
  type Mutate,
  type StoreApi,
  type UseBoundStore,
} from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";
import {
  type HistoryThreadRef,
  historyThreadKey,
  isHistoryThreadRef,
} from "@/features/history/historyIdentity";
import { resolveSafePersistStorage } from "@/shared/lib/safePersistStorage";

export interface ViewedItem extends HistoryThreadRef {
  ts: number;
  readReplyNumber: number;
}

export interface HistoryState {
  viewed: ViewedItem[];
  addViewed: (boardSlug: string, threadId: string) => void;
  removeFromHistory: (boardSlug: string, threadId: string) => void;
  clearHistory: (boardSlugs?: readonly string[]) => void;
  getViewedItems: () => ViewedItem[];
  getReadReplyNumber: (
    boardSlug: string,
    threadId: string,
  ) => number | undefined;
  recordReadReplyNumber: (
    boardSlug: string,
    threadId: string,
    readReplyNumber: number,
  ) => void;
  getUnreadCount: (
    boardSlug: string,
    threadId: string,
    repliesCount: number,
  ) => number | undefined;
}

export const MAX_VIEWED = 200;
const HISTORY_STORAGE_KEY = "nijiurachan-history-by-board-v1";
const HISTORY_STORAGE_VERSION = 1;
// 「0レス既読」を表す readReplyNumber。backend の OP seq は 0 開始。
const INITIAL_READ_REPLY_NUMBER = 0;

export function advanceReadReplyNumber(
  currentReadReplyNumber: number,
  nextReadReplyNumber: number,
): number {
  return Math.max(currentReadReplyNumber, nextReadReplyNumber);
}

export function unreadReplyCount(
  readReplyNumber: number,
  repliesCount: number,
): number | undefined {
  const unreadCount =
    repliesCount - (readReplyNumber - INITIAL_READ_REPLY_NUMBER);
  return unreadCount <= 0 ? undefined : unreadCount;
}

export type HistoryStore = UseBoundStore<
  Mutate<StoreApi<HistoryState>, [["zustand/persist", unknown]]>
>;

type HistoryRemoval = { keys: readonly (string | null)[]; ts: number };

export function createHistoryStore(storage?: StateStorage): HistoryStore {
  let clearingHistory = false;
  let removal: HistoryRemoval | undefined;
  try {
    storage ??= globalThis.localStorage;
  } catch {
    // localStorage自体を取得できない場合も、メモリ上の操作を継続する。
  }
  const persistedStorage = resolveSafePersistStorage(
    storage
      ? withMemoryFallback(
          storage,
          () => clearingHistory,
          () => removal,
        )
      : undefined,
  );

  return create<HistoryState>()(
    persist(
      (set, get) => ({
        viewed: [],

        addViewed(boardSlug: string, threadId: string): void {
          set((state) => {
            const latestViewed = readPersistedViewed(
              persistedStorage,
              state.viewed,
            );
            const existingViewed = latestViewed.find(
              (item) =>
                item.boardSlug === boardSlug && item.threadId === threadId,
            );
            const filtered = latestViewed.filter(
              (item) =>
                item.boardSlug !== boardSlug || item.threadId !== threadId,
            );
            const newViewed = [
              {
                boardSlug,
                threadId,
                ts: nowEpochMilliseconds(),
                readReplyNumber:
                  existingViewed?.readReplyNumber ?? INITIAL_READ_REPLY_NUMBER,
              },
              ...filtered,
            ];
            return { viewed: newViewed.slice(0, MAX_VIEWED) };
          });
        },

        removeFromHistory(boardSlug: string, threadId: string): void {
          const key = historyThreadKey({ boardSlug, threadId });
          removal = { keys: [key], ts: nowEpochMilliseconds() };
          try {
            set((state) => ({
              viewed: readPersistedViewed(
                persistedStorage,
                state.viewed,
              ).filter(
                (item) =>
                  item.boardSlug !== boardSlug || item.threadId !== threadId,
              ),
            }));
          } finally {
            removal = undefined;
          }
        },

        clearHistory: (boardSlugs?: readonly string[]) => {
          clearingHistory = boardSlugs === undefined;
          const removedAt = nowEpochMilliseconds();
          const isTarget = (item: HistoryThreadRef): boolean =>
            boardSlugs?.includes(item.boardSlug) ?? true;
          removal = { keys: [null], ts: removedAt };
          try {
            set((state) => {
              const latestViewed = readPersistedViewed(
                persistedStorage,
                state.viewed,
              );
              if (!boardSlugs) return { viewed: [] };
              removal = {
                keys: latestViewed.filter(isTarget).map(historyThreadKey),
                ts: removedAt,
              };
              return {
                viewed: latestViewed.filter((item) => !isTarget(item)),
              };
            });
          } finally {
            clearingHistory = false;
            removal = undefined;
          }
        },

        getViewedItems: () => get().viewed,

        getReadReplyNumber(
          boardSlug: string,
          threadId: string,
        ): number | undefined {
          return get().viewed.find(
            (item) =>
              item.boardSlug === boardSlug && item.threadId === threadId,
          )?.readReplyNumber;
        },

        recordReadReplyNumber(
          boardSlug: string,
          threadId: string,
          readReplyNumber: number,
        ): void {
          set((state) => ({
            // 指定のスレッドの既読レス数を記録する。増加のみ。
            // Post.seq の値をそのまま記録することを意図する。
            // 引数の readReplyNumber が記録値以下、または未閲覧スレなら何もしない。
            viewed: readPersistedViewed(persistedStorage, state.viewed).map(
              (item) =>
                item.boardSlug === boardSlug && item.threadId === threadId
                  ? {
                      ...item,
                      readReplyNumber: advanceReadReplyNumber(
                        item.readReplyNumber,
                        readReplyNumber,
                      ),
                    }
                  : item,
            ),
          }));
        },

        getUnreadCount(
          boardSlug: string,
          threadId: string,
          repliesCount: number,
        ): number | undefined {
          const readReplyNumber = get().getReadReplyNumber(boardSlug, threadId);
          if (readReplyNumber == null) {
            return undefined;
          } else {
            // 記録値は Post.seq の値そのままなので、それを踏まえて調整する。
            // 0以下はundefinedにする
            return unreadReplyCount(readReplyNumber, repliesCount);
          }
        },
      }),
      {
        name: HISTORY_STORAGE_KEY,
        version: HISTORY_STORAGE_VERSION,
        storage: createJSONStorage(() => persistedStorage),
        merge: (persistedState, currentState) => ({
          ...currentState,
          viewed:
            isRecord(persistedState) &&
            Array.isArray(persistedState.viewed) &&
            persistedState.viewed.every(isViewedItem)
              ? persistedState.viewed
              : currentState.viewed,
        }),
      },
    ),
  );
}

export const useHistoryStore: HistoryStore = createHistoryStore();

function readPersistedViewed(
  storage: StateStorage,
  fallback: ViewedItem[],
): ViewedItem[] {
  const serialized = storage.getItem(HISTORY_STORAGE_KEY);
  // タブ間の最新値反映は同期storageが対象。非同期storageは現在stateを使う。
  if (typeof serialized !== "string") return fallback;
  return parseViewed(serialized) ?? fallback;
}

function parseViewed(
  serialized: string | null | undefined,
): ViewedItem[] | undefined {
  if (serialized === null) return [];
  if (serialized === undefined) return undefined;
  try {
    const persisted = JSON.parse(serialized) as unknown;
    if (!isRecord(persisted) || persisted.version !== HISTORY_STORAGE_VERSION) {
      return undefined;
    }
    const state = persisted.state;
    if (
      !isRecord(state) ||
      !Array.isArray(state.viewed) ||
      !state.viewed.every(isViewedItem)
    ) {
      return undefined;
    }
    return state.viewed;
  } catch {
    return undefined;
  }
}

function mergePendingHistory(
  base: string | null | undefined,
  local: string | null,
  external: string | null,
  completedWrites: Iterable<string | null>,
  removals: ReadonlyMap<string | null, number>,
  acceptedRevisits: Map<string, number>,
): string | null {
  const baseItems = parseViewed(base ?? null);
  const localItems = parseViewed(local);
  const externalItems = parseViewed(external);
  if (!baseItems || !localItems || !externalItems) return external;

  const baseByKey = new Map(
    baseItems.map((item) => [historyThreadKey(item), item]),
  );
  // 先行保存済みの追加も基点へ含め、後続操作が観測した基点は保持する。
  for (const completed of completedWrites) {
    for (const item of parseViewed(completed) ?? []) {
      const key = historyThreadKey(item);
      const previous = baseByKey.get(key);
      if (!previous || item.ts > previous.ts) baseByKey.set(key, item);
    }
  }
  const localByKey = new Map(
    localItems.map((item) => [historyThreadKey(item), item]),
  );
  const externalByKey = new Map(
    externalItems.map((item) => [historyThreadKey(item), item]),
  );
  const merged: ViewedItem[] = [];
  for (const key of new Set([...externalByKey.keys(), ...localByKey.keys()])) {
    const own = localByKey.get(key);
    const other = externalByKey.get(key);
    const original = baseByKey.get(key);
    const removedAt = Math.max(
      removals.get(key) ?? -Infinity,
      removals.get(null) ?? -Infinity,
    );
    const acceptedTs = acceptedRevisits.get(key);
    // 削除前の保存値は、再追加した自タブの既読位置にも混ぜない。
    // 基点が不明なら未来時刻だけで削除を取り消さない。
    if (
      other &&
      removedAt !== -Infinity &&
      (base === undefined ||
        other.ts <= removedAt ||
        (acceptedTs !== undefined
          ? other.ts < acceptedTs
          : original && other.ts <= original.ts))
    ) {
      if (own) merged.push(own);
      continue;
    }
    // 受理済みの再閲覧は、基点が進んでもその後の既読更新を受け入れる。
    if (other && removedAt !== -Infinity) {
      acceptedRevisits.set(key, Math.max(acceptedTs ?? -Infinity, other.ts));
    }
    // 件数上限で外れた履歴も、別タブの再閲覧なら保持する。
    if (!own && other && original && other.ts <= original.ts) continue;
    if (own && !other && original) continue;
    if (own && other) {
      merged.push({
        boardSlug: own.boardSlug,
        threadId: own.threadId,
        ts: Math.max(own.ts, other.ts),
        readReplyNumber: Math.max(own.readReplyNumber, other.readReplyNumber),
      });
    } else if (own) {
      merged.push(own);
    } else if (other) {
      merged.push(other);
    }
  }
  merged.sort((a, b) => b.ts - a.ts);
  return JSON.stringify({
    state: { viewed: merged.slice(0, MAX_VIEWED) },
    version: HISTORY_STORAGE_VERSION,
  });
}

function isViewedItem(value: unknown): value is ViewedItem {
  return (
    isRecord(value) &&
    isHistoryThreadRef(value) &&
    typeof value.ts === "number" &&
    Number.isFinite(value.ts) &&
    typeof value.readReplyNumber === "number" &&
    Number.isFinite(value.readReplyNumber)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function withMemoryFallback(
  storage: StateStorage,
  isClearingHistory: () => boolean,
  getRemoval: () => HistoryRemoval | undefined,
): StateStorage {
  type StoredValue = string | null | undefined;
  type PendingWrite = {
    value: string | null;
    base: StoredValue | Promise<StoredValue>;
    completedWrites: Set<string | null>;
    removals: Map<string | null, number>;
    acceptedRevisits: Map<string, number>;
  };
  const shadow = new Map<string, PendingWrite>();
  const lastStored = new Map<string, string | null>();
  const revisions = new Map<string, number>();
  const writeRevisions = new Map<string, number>();
  const writes = new Map<string, Promise<void>>();

  function nextRevision(name: string): number {
    const revision = (revisions.get(name) ?? 0) + 1;
    revisions.set(name, revision);
    return revision;
  }

  function readStored(name: string): StoredValue | Promise<StoredValue> {
    const revision = nextRevision(name);
    const remember = (value: string | null): string | null => {
      if (revisions.get(name) === revision) lastStored.set(name, value);
      return value;
    };
    try {
      const stored = storage.getItem(name);
      return stored instanceof Promise
        ? stored.then(remember, () => undefined)
        : remember(stored);
    } catch {
      // 取得失敗を「保存値なし」のnullと区別する。
      return undefined;
    }
  }

  function reconcile(
    name: string,
    pending: PendingWrite,
    stored: StoredValue,
    base: StoredValue,
  ): string | null {
    // キュー中は保存値との一致だけで退避を解放しない。先行保存で変わり得る。
    if (writes.has(name)) return pending.value;
    if (stored === undefined) return pending.value;
    if (stored === pending.value) {
      if (shadow.get(name) === pending) shadow.delete(name);
      return stored;
    }
    // 不正な外部値では、未保存の履歴と削除情報を解放しない。
    if (parseViewed(stored) === undefined) return pending.value;
    // 保存失敗後の退避は、保存先が変わっていない間だけ使う。
    if (
      stored !== pending.value &&
      (stored === base || pending.completedWrites.has(stored))
    ) {
      return pending.value;
    }
    const merged = mergePendingHistory(
      base,
      pending.value,
      stored,
      pending.completedWrites,
      pending.removals,
      pending.acceptedRevisits,
    );
    if (merged === stored) {
      if (shadow.get(name) === pending) shadow.delete(name);
    } else {
      pending.value = merged;
      pending.base = stored;
      pending.completedWrites.clear();
    }
    return merged;
  }

  function write(
    name: string,
    value: string | null,
    operation: () => unknown,
  ): unknown {
    writeRevisions.set(name, (writeRevisions.get(name) ?? 0) + 1);
    const previous = lastStored.get(name);
    const stored = readStored(name);
    const baseline = (current: StoredValue): StoredValue =>
      current === undefined ? previous : current;
    const previousPending = shadow.get(name);
    const removals = new Map(previousPending?.removals);
    const acceptedRevisits = new Map(previousPending?.acceptedRevisits);
    const removal = getRemoval();
    if (removal) {
      for (const key of removal.keys) {
        removals.set(key, removal.ts);
        if (key === null) acceptedRevisits.clear();
        else acceptedRevisits.delete(key);
      }
    }
    if (value === null) {
      removals.set(null, nowEpochMilliseconds());
      acceptedRevisits.clear();
    }
    const pending: PendingWrite = {
      value,
      // 全削除では直前に保存されている履歴も削除対象に含める。
      base:
        previousPending && !isClearingHistory()
          ? previousPending.base
          : stored instanceof Promise
            ? stored.then(baseline)
            : baseline(stored),
      completedWrites: new Set(previousPending?.completedWrites),
      removals,
      acceptedRevisits,
    };
    shadow.set(name, pending);
    // 例外は外側のsafe storageで処理する。成功した退避は即座に解放する。
    const confirm = (): void => {
      // 保存前に始まった非同期readで、確認済みの保存値を巻き戻さない。
      nextRevision(name);
      const latest = shadow.get(name);
      if (latest === pending) {
        lastStored.set(name, value);
        shadow.delete(name);
      } else if (latest) {
        // 先行する自タブの保存完了を、他タブによる変更と区別する。
        latest.completedWrites.add(value);
      }
    };
    const execute = (): void | Promise<void> => {
      const result = operation();
      return result instanceof Promise ? result.then(confirm) : confirm();
    };
    const previousWrite = writes.get(name);
    // 同期localStorageはその場で保存し、非同期の保存だけを直列化する。
    const result = previousWrite ? previousWrite.then(execute) : execute();
    if (result instanceof Promise) {
      const settled = result.then(
        () => undefined,
        () => undefined,
      );
      writes.set(name, settled);
      void settled.then(() => {
        if (writes.get(name) === settled) writes.delete(name);
      });
    }
    return result;
  }

  return {
    getItem(name: string): string | null | Promise<string | null> {
      const writeRevision = writeRevisions.get(name);
      const stored = readStored(name);
      const pending = shadow.get(name);
      if (!pending) {
        return stored instanceof Promise
          ? stored.then((value) =>
              writeRevisions.get(name) === writeRevision
                ? (value ?? null)
                : null,
            )
          : (stored ?? null);
      }
      if (stored instanceof Promise || pending.base instanceof Promise) {
        void Promise.all([stored, pending.base]).then(([value, base]) => {
          if (shadow.get(name) === pending) {
            reconcile(name, pending, value, base);
          }
        });
        // 退避値は同期で返し、遅れたhydrateが後続操作を巻き戻さないようにする。
        return pending.value;
      }
      return reconcile(name, pending, stored, pending.base);
    },
    setItem(name: string, value: string): unknown {
      return write(name, value, () => storage.setItem(name, value));
    },
    removeItem(name: string): unknown {
      return write(name, null, () => storage.removeItem(name));
    },
  };
}

export function useViewedHistoryItem(
  boardSlug: string,
  threadId: string,
): ViewedItem | undefined {
  return useHistoryStore((state) =>
    state.viewed.find(
      (item) => item.boardSlug === boardSlug && item.threadId === threadId,
    ),
  );
}
